import { useMemo, useEffect, useState, useRef } from "react";
import axios from "axios";
import { CircularProgress, Pagination, Box } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import Readmore from "../Dashboard/Readmore";
import CustomdateC from "../Dashboard/CustomdateC";
import dayjs from "dayjs";
import ExportS from "../Dashboard/ExportS";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import Question from "../Dashboard/Question";
import { API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import { getDomainId } from "utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryParams } from "context/QueryParamsContext";
import ExportC from "../Dashboard/ExportC";
import { enUS } from "date-fns/locale";
import { useDomain } from "context/DomainContext";
import CustomdateD from "../Dashboard/CustomdateD";

function DataTable({
  entriesPerPageOptions = [5, 10, 15, 20, 25],
  canSearch = false,
  date = false,
  csv = false,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  dashboardDateRange,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const [rows, setRows] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const location = useLocation();
  const isFirstLoading = useRef(true);
  const [hoveredCountry, setHoveredCountry] = useState("");
      const navigate= useNavigate();

  const { dateRange, setDateRange } = useQueryParams();

  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const start = (page - 1) * entriesPerPage + 1;
  const end = Math.min(page * entriesPerPage, totalEntries);
       const { domainId } = useDomain();
              if(!domainId){
        navigate("/Access-Denied")
       }


  useEffect(() => {
    if (dashboardDateRange?.start_date && dashboardDateRange?.end_date) {
      setDateRange(dashboardDateRange);
    }
  }, [dashboardDateRange, setDateRange]);

  const fetchUsers = async () => {
    if (isFirstLoading.current) setLoading(true);

    try {
      const { start_date, end_date } = dateRange;
      const offset = (page - 1) * entriesPerPage;

      const res = await axios.get(`${LAMBDA_BASE_URL}/contact-us-actions`, {
        params: {
          limit: entriesPerPage,
          offset,
          search,
          sort_key: sortConfig.key,
          sort_direction: sortConfig.direction,
          start_date,
          end_date,
        },
        headers: { "DOMAIN-TOKEN": domainId },
      });

      const formattedRows = res.data.result.contact_us_actions.map((user) => ({
        id: user.id || `default-${user.question}`,
        ip: user.ip,
        city: user.city,
        region: user.region,
        country_flag: user.country_flag,
        country: user.country,
        created_at: dayjs(user.datetime).format("MMM D, YYYY hh:mm A"),

      }));

      setRows(formattedRows);
      setTotalEntries(res.data.result.total);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      if (isFirstLoading.current) {
        setLoading(false);
        isFirstLoading.current = false;
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, entriesPerPage, search, sortConfig, dateRange,domainId]);

  const handlePageChange = (event, value) => setPage(value);

  const handleEntriesPerPageChange = ({ value }) => {
    setEntriesPerPage(value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const handleDateRangeSelect = ({ start, end }) => {
    setDateRange({ start_date: start, end_date: end });
    sessionStorage.setItem('saved_start_date', start);
    sessionStorage.setItem('saved_end_date', end);
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('start_date', start);
    queryParams.set('end_date', end);
    navigate({ pathname: location.pathname, search: queryParams.toString() });
  };

  return (
    <TableContainer sx={{ boxShadow: "none", minHeight: "450px" }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress color="teal" />
        </Box>
      ) : (
        <>
        <h4 style={{ textAlign: "left", paddingBottom: "0px" }}> Contact Us Actions</h4>
          {(entriesPerPageOptions || canSearch || date || csv) && (
        <SoftBox
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          gap={2}
          p={2}
        >
          <SoftBox display="flex" alignItems="center" sx={{ width: { xs: "100%", md: "auto" } }}>
            <SoftSelect
              defaultValue={{ value: entriesPerPage, label: entriesPerPage }}
              options={entriesPerPageOptions.map((entry) => ({
                value: entry,
                label: entry,
              }))}
              onChange={handleEntriesPerPageChange}
              size="small"
            />
            <SoftTypography variant="caption" color="secondary" ml={1}>
              entries per page
            </SoftTypography>
          </SoftBox>
          <SoftBox
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
            width={{ xs: "100%", md: "auto" }}
          >    
            {canSearch && (
              <SoftBox sx={{ width: { xs: "100%", md: "12rem" } }}>
                <SoftInput
                  placeholder="Search..."
                  value={search}
                  onChange={handleSearchChange}
                  fullWidth
                />
              </SoftBox>
            )} 
            {date && (
              <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                <CustomdateD onRangeSelect={handleDateRangeSelect} />
              </SoftBox>
            )}        
            {csv && (
              <SoftBox sx={{ width: { xs: "100%", md: "auto" }, ml: { xs: 0, md: 0 } }}>
                  <ExportC
                    searchQuery={search}
                    startDate={dateRange.start_date}
                    endDate={dateRange.end_date}
                    filename="contact_us_actions_details.csv"
                    columnDefs={columns}
                  />
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
          )}

          <Table>
            <SoftBox component="thead">
              {columns.map((column, key) => {
                const isSortable = !["Content", "Likes/Dislikes"].includes(column.Header);

                return (
                  <DataTableHeadCell
                    key={key}
                    width={column.width || "auto"}
                    align={column.align || "left"}
                    onClick={isSortable ? () => handleSort(column.accessor) : undefined}
                    sorted={
                      isSortable && sortConfig.key === column.accessor
                        ? sortConfig.direction
                        : "none"
                    }
                    showSortIcon={!["content", "likes_dislikes"].includes(column.accessor)}
                    style={{ cursor: isSortable ? "pointer" : "default", userSelect: "none" }}
                  >
                    {column.Header}
                  </DataTableHeadCell>
                );
              })}
            </SoftBox>
            <TableBody>
              {rows.map((row, key) => (
                <TableRow key={key}>
                  {Object.entries(row).map(([colKey, cell], index) => {
                    if (["id", "country", "city", "region"].includes(colKey)) return null;

                    return (
                      <DataTableBodyCell key={index} align="left" colSpan={colKey === "content" ? columns.length : 1}>
                        {colKey === "content" ? (
                          <>
                            <div style={{ display: "flex" }}>
                             
                              <strong>{cell.question}</strong>
                            </div>
                            
                          </>
                        ) : colKey === "country_flag" ? (
                          <div
                            style={{ display: "flex", alignItems: "flex-start", gap: "10px", position: "relative" }}
                            onMouseEnter={() => setHoveredCountry(row.id)}
                            onMouseLeave={() => setHoveredCountry(null)}
                          >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                              <img
                                src={cell}
                                alt="flag"
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  objectFit: "cover",
                                  borderRadius: "100%",
                                  cursor: "pointer",
                                }}
                              />
                              <div
                                style={{
                                  opacity: hoveredCountry === row.id ? 1 : 0,
                                  position: "absolute",
                                  top: "110%",
                                  left: "40%",
                                  transform: "translateX(-50%)",
                                  backgroundColor: "#fff",
                                  padding: "4px 8px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                  borderRadius: "4px",
                                  whiteSpace: "nowrap",
                                  fontSize: "0.75rem",
                                  zIndex: 10,
                                }}
                              >
                                <span style={{ paddingTop: "5px" }}>{row.country}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", fontSize: "0.75rem" }}>
                              <span>{row.city},</span>
                              <span style={{ paddingTop: "5px" }}>{row.region}</span>
                            </div>
                          </div>
                        ) 
                        : colKey === "likes_dislikes" ? (
                          <td style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {cell.likes === 1 && cell.dislikes === 0 ? (
                              <ThumbsUp color="green" size={20} />
                            ) : cell.dislikes === 1 && cell.likes === 0 ? (
                              <ThumbsDown color="red" size={20} />
                            ) : cell.likes === 0 && cell.dislikes === 0 ? (
                              <ThumbsUp color="gray" size={20} />
                            ) : null}
                          </td>
                        ) 
                        : colKey === "ip" ? (
                          <a
                            href={`https://whatismyipaddress.com/ip/${cell}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#007bff", textDecoration: "underline" }}
                          >
                            {cell}
                          </a>
                        ) : colKey === "answer" ? (
                                          <Readmore text={row.answer} apiEndpoint="/api/edit-answer" id={row.id} />
                                        ) : (
                                          cell
                                        )}
                            </DataTableBodyCell>
                          );
                        })}

                                  </TableRow>
                                ))}
            </TableBody>

            <SoftBox pb={1} display="flex" justifyContent="space-between" alignItems="center" paddingTop="10px" paddingLeft="40px">
              <SoftTypography variant="button" color="secondary">
                Showing {new Intl.NumberFormat(enUS).format(start)} to {new Intl.NumberFormat(enUS).format(end)} of {new Intl.NumberFormat(enUS).format(totalEntries)} entries
              </SoftTypography>
            </SoftBox>
          </Table>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="circular"
              color="info"
              sx={{
                
                "& .MuiPaginationItem-root": {
                  "&.Mui-selected": {
                    background: "#219297",
                    color: "white !important", 
                    "&:hover": {
                      background: "teal",
                    },
                  },
                  "&:not(.Mui-selected)": {
                    color: "black",
                  },
                },
              }}
            />
            <SoftTypography variant="button">Page {new Intl.NumberFormat(enUS).format(page)} of {new Intl.NumberFormat(enUS).format(totalPages)}</SoftTypography>
          </SoftBox>
        </>
      )}
    </TableContainer>
  );
}

export default DataTable;
